import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { VerticalAlignBottomOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';

import { getScrollContainer, getScrollShortcuts } from '../utils/scroll';

const partsList = () => document.querySelector('[data-testid="parts-and-specifications"]');

/**
 * Staff "go to top" / "go to bottom" shortcuts. They sit left of the floating chat
 * button (bottom-right, 56px) and below the upload tray, and only show when useful.
 */
export const ScrollShortcuts = () => {
    const [shortcuts, setShortcuts] = useState({ canGoTop: false, canGoBottom: false });

    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            const container = getScrollContainer(partsList());
            const next = getScrollShortcuts(container);
            setShortcuts(current =>
                current.canGoTop === next.canGoTop && current.canGoBottom === next.canGoBottom ? current : next
            );
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        schedule();
        document.addEventListener('scroll', schedule, { capture: true, passive: true });
        window.addEventListener('resize', schedule);
        // Content height changes as parts load, expand or collapse.
        const observer = new ResizeObserver(schedule);
        observer.observe(document.body);
        return () => {
            document.removeEventListener('scroll', schedule, { capture: true });
            window.removeEventListener('resize', schedule);
            observer.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    const jump = (to: 'top' | 'bottom') => {
        const container = getScrollContainer(partsList());
        container.scrollTo({ top: to === 'top' ? 0 : container.scrollHeight, behavior: 'auto' });
    };

    if (!shortcuts.canGoTop && !shortcuts.canGoBottom) return null;

    return (
        <div
            data-testid="otf-scroll-shortcuts"
            style={{
                position: 'fixed',
                // Chat button: right 24px, bottom 24px, 56px. Sit 12px to its left and
                // vertically centered with it (40px buttons).
                right: 92,
                bottom: 32,
                zIndex: 1050,
                display: 'flex',
                gap: 8,
            }}
        >
            {shortcuts.canGoTop ? (
                <Tooltip title="Go to top" placement="top">
                    <Button
                        aria-label="Go to top"
                        data-testid="otf-scroll-to-top"
                        shape="circle"
                        size="large"
                        icon={<VerticalAlignTopOutlined />}
                        onClick={() => jump('top')}
                        className="otf-scroll-shortcut"
                    />
                </Tooltip>
            ) : null}
            {shortcuts.canGoBottom ? (
                <Tooltip title="Go to bottom" placement="top">
                    <Button
                        aria-label="Go to bottom"
                        data-testid="otf-scroll-to-bottom"
                        shape="circle"
                        size="large"
                        icon={<VerticalAlignBottomOutlined />}
                        onClick={() => jump('bottom')}
                        className="otf-scroll-shortcut"
                    />
                </Tooltip>
            ) : null}
        </div>
    );
};

export default ScrollShortcuts;
